import React, { useState, Fragment } from "react";
import { toast } from "react-toastify";
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';

import { LoadingDots } from "../components/LoadingDots";
import { toastErrors } from "../utils/toastErrors";

// Apps Script (conta lucca@arkspace.ai) que grava cada lead na planilha
// "Arkspace — Leads do site (formulário de contato)".
const CONTACT_ENDPOINT = 'https://script.google.com/macros/s/AKfycbzl-TKa2VmiKHt-jFPghpbMoNsVzisXy19_YdvLic3-B2PQpo2CrICjo6iKMBW96h1gMA/exec';

type listT = {
  id: number,
  content: string
}

const categories: listT[] = [
  { id: 1, content: 'Arkspace Labs (Enterprise)' },
  { id: 2, content: 'Arkspace Go (PMEs)' },
  { id: 3, content: 'Parcerias' },
  { id: 4, content: 'Outros' },
]

const expectedMessages: listT[] = [
  { id: 1, content: 'Não sei informar' },
  { id: 2, content: 'Até 100.000' },
  { id: 3, content: 'Entre 100.000 e 500.000' },
  { id: 4, content: 'Entre 500.000 e 1.000.000' },
  { id: 5, content: 'Acima de 1.000.000' },
]

const Contact = () => {
  const [name, setName] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<listT>();
  const [selectedExpectedMessages, setSelectedExpectedMessages] = useState<listT>();

  const [loading, setLoading] = React.useState<boolean>(false);

  const formatPhoneNumber = (value: string): string => {
    const cleaned = ('' + value).replace(/\D/g, '');

    if (cleaned.length <= 2) {
      return `(${cleaned}`;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2)}`;
    } else if (cleaned.length <= 10) {
      return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 6)}-${cleaned.substring(6)}`;
    } else if (cleaned.length <= 11) {
      return `(${cleaned.substring(0, 2)}) ${cleaned[2]} ${cleaned.substring(3, 7)}-${cleaned.substring(7)}`;
    }

    return value;
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formattedValue = formatPhoneNumber(value);
    setPhoneNumber(formattedValue);
  };

  const isValidEmail = (email: string): boolean => {
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return regex.test(email);
  };

  const handleForm = async () => {
    const toastId = toast.loading("Aguarde...");
    setLoading(true);

    if (!isValidEmail(email)) {
      toastErrors(toastId, { message: 'Email inválido!' });
      setLoading(false);
      return;
    }

    if (!name || !email || !phoneNumber || !selectedExpectedMessages || !selectedCategory) {
      toastErrors(toastId, { message: 'Preencha todos os campos!' });
      setLoading(false);
      return;
    }

    const data = {
      name: name,
      company: company,
      email: email,
      phoneNumber: phoneNumber,
      expectedMessages: selectedExpectedMessages?.content,
      category: selectedCategory?.content
    };

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        // text/plain evita o preflight CORS (o Apps Script não responde a OPTIONS).
        // Sem 'no-cors' conseguimos ler a resposta e detectar falhas de verdade.
        headers: {
          'Content-Type': 'text/plain;charset=utf-8',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Falha no envio (HTTP ${response.status}).`);
      }

      const result = await response.json();

      if (!result?.ok) {
        throw new Error(result?.error || 'Não foi possível registrar seu contato.');
      }

      toast.update(toastId, {
        render: 'Agradecemos o contato! Retornaremos em breve.',
        type: "success",
        isLoading: false,
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnFocusLoss: true,
        draggable: true,
        pauseOnHover: true
      });

      setLoading(false);
    } catch (error) {
      console.error('Falha ao enviar o formulário de contato:', error);
      toastErrors(toastId, {
        message: 'Não foi possível enviar sua mensagem. Tente novamente ou escreva para contato@arkspace.ai.'
      });
      setLoading(false);
    }
  }

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-[#070324] overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid opacity-20" />

      {/* Gradient Orbs */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#1f2fd6] rounded-full filter blur-[128px] opacity-10" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#0c1aa6] rounded-full filter blur-[128px] opacity-10" />

      <div className="relative z-10 mx-auto max-w-7xl px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">

          {/* Left Content */}
          <div className="lg:sticky lg:top-32">
            {/* Badge */}
            <span className="text-sm font-medium text-[#1f2fd6] uppercase tracking-wider">
              Contato
            </span>

            {/* Headline */}
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
              Vamos construir o
              <br />
              <span className="gradient-text">futuro juntos?</span>
            </h2>

            {/* Description */}
            <p className="mt-6 text-lg text-[#868686] max-w-lg">
              Preencha o formulário e nosso time entrará em contato para entender suas necessidades e demonstrar como a ArkSpace pode impulsionar sua operação.
            </p>

            {/* Features */}
            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1f2fd6]/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#1f2fd6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Soluções sob medida</h4>
                  <p className="text-sm text-[#868686]">
                    Desenvolvemos soluções personalizadas para suas necessidades específicas.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1f2fd6]/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#1f2fd6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Resposta em 24h</h4>
                  <p className="text-sm text-[#868686]">
                    Nosso time responde rapidamente para iniciar sua jornada de transformação.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1f2fd6]/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#1f2fd6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Dados protegidos</h4>
                  <p className="text-sm text-[#868686]">
                    Suas informações são tratadas com total segurança e confidencialidade.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Form */}
          <div>
            <div className="glass rounded-2xl p-8 lg:p-10">
              <div className="space-y-5">
                <ContactInputBox
                  type="text"
                  name="name"
                  placeholder="Seu nome completo"
                  onChange={(e: any) => setName(e.target.value)}
                  value={!name ? '' : name}
                />
                <ContactInputBox
                  type="text"
                  name="company"
                  placeholder="Nome da empresa"
                  onChange={(e: any) => setCompany(e.target.value)}
                  value={!company ? '' : company}
                />
                <ContactInputBox
                  type="text"
                  name="email"
                  placeholder="Email comercial"
                  onChange={(e: any) => setEmail(e.target.value)}
                  value={!email ? '' : email}
                />
                <ContactInputBox
                  type="text"
                  name="phone"
                  placeholder="Telefone de contato"
                  maxLength={16}
                  onChange={handlePhoneNumberChange}
                  value={!phoneNumber ? '' : phoneNumber}
                />
                <div className="relative z-20">
                  <ListInputBox
                    list={categories}
                    placeholder="Como podemos te ajudar?"
                    selected={selectedCategory}
                    setSelected={setSelectedCategory}
                  />
                </div>
                <div className="relative z-10">
                  <ListInputBox
                    list={expectedMessages}
                    placeholder="Expectativa de mensagens mensais"
                    selected={selectedExpectedMessages}
                    setSelected={setSelectedExpectedMessages}
                  />
                </div>

                <button
                  type="submit"
                  onClick={handleForm}
                  className="w-full py-4 bg-[#1f2fd6] text-white font-medium rounded-xl hover:bg-[#0c1aa6] transition-all hover:shadow-lg hover:shadow-[#1f2fd6]/25"
                >
                  {loading ? <LoadingDots color="white" dotStyle="small" /> : 'Enviar mensagem'}
                </button>

                <p className="text-xs text-[#868686] text-center">
                  Ao enviar, você concorda com nossa{' '}
                  <a href="/privacy" className="text-[#1f2fd6] hover:underline">
                    Política de Privacidade
                  </a>
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;

const ContactInputBox = ({ type, placeholder, name, value, onChange, maxLength }: any) => {
  return (
    <div>
      <input
        type={type}
        placeholder={placeholder}
        name={name}
        maxLength={maxLength}
        value={value}
        onChange={onChange}
        className="w-full px-4 py-4 bg-[#1a1f4a] border border-[#1f2fd6]/30 rounded-xl text-white placeholder-[#a8b4d4] outline-none focus:border-[#1f2fd6] focus:ring-1 focus:ring-[#1f2fd6]/50 transition-all"
      />
    </div>
  );
};

const ListInputBox = ({ list, placeholder, selected, setSelected }: any) => {
  return (
    <Listbox value={selected} onChange={setSelected}>
      <div className="relative">
        <Listbox.Button className="relative cursor-pointer w-full px-4 py-4 bg-[#1a1f4a] border border-[#1f2fd6]/30 rounded-xl text-left outline-none focus:border-[#1f2fd6] focus:ring-1 focus:ring-[#1f2fd6]/50 transition-all">
          <span className={`block truncate ${selected?.content ? 'text-white' : 'text-[#a8b4d4]'}`}>
            {selected?.content ? selected?.content : placeholder}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
            <ChevronUpDownIcon className="h-5 w-5 text-[#a8b4d4]" aria-hidden="true" />
          </span>
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute mt-2 max-h-60 w-full overflow-auto rounded-xl bg-[#1a1f4a] border border-[#1f2fd6]/30 py-2 shadow-lg shadow-black/20 focus:outline-none">
            {list.map((item: listT, index: number) => (
              <Listbox.Option
                key={index}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-3 pl-10 pr-4 ${
                    active ? 'bg-[#1f2fd6]/30 text-white' : 'text-[#c1d5ed]'
                  }`
                }
                value={item}
              >
                {({ selected, active }) => (
                  <>
                    <span className={`block truncate ${selected ? 'font-medium text-white' : 'font-normal'}`}>
                      {item.content}
                    </span>
                    {selected ? (
                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-[#1f2fd6]'}`}>
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  )
}
